import styled from "styled-components";
import { BREAKPOINT_MAX } from "../../../styles/responsiveBreakpoints";
import { getFlightColor } from '../utils/colorHelpers';

export const ItineraryCard = styled.div`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  background: ${({ theme }) => theme.tokens.color.surface.primary};
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;

  @media (max-width: ${BREAKPOINT_MAX.md}px) {
    padding: 1rem;
  }
`;

export const SectionTitleText = styled.div`
  margin-bottom: 1.5rem;
  
  &:first-child {
    margin-top: 0;
  }
`;

export const ItineraryColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  min-width: 0;

  @media (max-width: ${BREAKPOINT_MAX.lg}px) {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }
`;

export const ItineraryColumn = styled.div`
  position: relative;
`;

export const ItineraryLabel = styled.p`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.tokens.color.text.primary};
  margin: 0 0 0.75rem 0;
  font-family: 'LATAM Sans Regular', 'LATAM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
`;

export const FlightSegmentsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

export const FlightSegment = styled.div<{ 
  variant?: 'previous' | 'new';
  $isFirst?: boolean;
  $isLast?: boolean;
}>`
  border: 1px solid ${({ theme }) => theme.tokens.color.border.primary};
  border-left: 6px solid ${({ variant, theme }) =>
    variant === 'new' ? theme.tokens.color.interaction.mediumDefault : theme.tokens.color.neutral['200']
  };
  border-top: ${({ $isFirst }) => $isFirst ? '1px solid' : 'none'};
  border-top-color: ${({ theme }) => theme.tokens.color.border.primary};
  border-bottom-color: ${({ theme }) => theme.tokens.color.border.primary};
  border-right: 1px solid ${({ theme }) => theme.tokens.color.border.primary};
  border-radius: ${({ $isFirst, $isLast }) => {
    if ($isFirst && $isLast) {
      return '0.25rem 0 0 0.25rem';
    }
    if ($isFirst) {
      return '0.25rem 0 0 0';
    }
    if ($isLast) {
      return '0 0 0 0.25rem';
    }
    return '0';
  }};
  padding: 1rem 1rem 1rem 0.875rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  
  &:not(:first-child) {
    margin-top: -1px;
  }

  border-top-right-radius: .25rem !important;
  border-bottom-right-radius: .25rem !important;
`;

export const FlightDetailsContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
  min-width: 0;
`;

export const FlightInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 80px;
`;

export const FlightNumber = styled.span<{ variant?: 'previous' | 'new' }>`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.tokens.color.text.primary};
  font-family: 'LATAM Sans Regular', 'LATAM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
`;

export const FlightMeta = styled.span`
  font-size: 1rem;
  color: ${({ theme }) => theme.tokens.color.text.primary};
  font-family: 'LATAM Sans Regular', 'LATAM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
`;

export const FlightTimes = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  flex: 1;
  min-width: 0;
  justify-content: space-around;
`;

export const FlightDateTime = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

export const FlightTimeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

export const AirportCode = styled.span<{ variant?: 'previous' | 'new'; $isFlown?: boolean }>`
  font-size: 18px;
  font-weight: 400;
  color: ${({ variant, $isFlown, theme }) => getFlightColor(theme, variant, $isFlown)};
  font-family: 'LATAM Sans Regular', 'LATAM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
`;

export const FlightTime = styled.span<{ variant?: 'previous' | 'new'; $isFlown?: boolean }>`
  font-size: 18px;
  font-weight: 400;
  color: ${({ variant, $isFlown, theme }) => getFlightColor(theme, variant, $isFlown)};
  font-family: 'LATAM Sans Regular', 'LATAM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
`;

export const FlightDate = styled.span<{ variant?: 'previous' | 'new' }>`
  font-size: 14px;
  color: ${({ theme }) => theme.tokens.color.text.secondary};
  font-family: 'LATAM Sans Regular', 'LATAM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
`;

export const FlightArrow = styled.span`
  color: ${({ theme }) => theme.tokens.color.text.secondary};
  font-size: 1rem;
`;

export const EmptyMessage = styled.div`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.tokens.color.text.secondary};
  font-style: italic;
  padding: 1rem;
  text-align: center;
`;

export const PartializedLoadBadge = styled.div`
  position: absolute;
  top: -30px;
  right: 0;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  background-color: ${({ theme }) => theme.tokens.color.surface.infoSoft};
  padding: 0.4rem 0.5rem;
  border-radius: 1rem;
  font-family: 'LATAM Sans Regular', 'LATAM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  font-weight: bold;
  z-index: 10;
`;

export const PartializedLoadIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  
  svg {
    display: block;
    width: 18px;
    height: 18px;
    transform: rotate(180deg);
  }
`;

export const PartializedLoadText = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.tokens.color.text.tertiary};
  font-family: 'LATAM Sans Regular', 'LATAM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  font-weight: 600;
`;

export const FlownStatus = styled.span`
  font-weight: 400;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.tokens.color.text.secondary};
  font-family: 'LATAM Sans Regular', 'LATAM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
`;